import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  FaArrowLeft,
  FaComments,
  FaHeartbeat,
  FaNotesMedical,
  FaPhoneAlt,
  FaRobot,
  FaUserNurse,
  FaUsers
} from "react-icons/fa";
import config from "../config";
import WorkerSidebarShell from "./WorkerSidebarShell";

const TOOL_LABELS = {
  get_elder_profile: "Resident profile",
  get_resident_profile: "Resident profile",
  get_care_fields: "Care fields",
  get_family_content: "Family context",
  get_recent_interactions: "Recent interactions",
  get_activated_workers: "Activated workers",
  get_resident_group_context: "Group context",
  get_resident_profile_document: "Profile document",
  update_medications: "Update medications",
  update_chronic_conditions: "Update conditions",
  update_family_contact: "Update family contact",
  update_care_notes: "Update care notes",
  draft_email: "Draft family updates",
  send_email: "Send email",
  send_family_notification: "Notify family",
  log_conversation: "Log conversation",
  log_mood_observation: "Log observations"
};

const WRITE_TOOLS = new Set([
  "update_medications",
  "update_chronic_conditions",
  "update_family_contact",
  "update_care_notes",
  "send_email",
  "send_family_notification",
  "log_conversation",
  "log_mood_observation"
]);

const WorkerElderProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { patientId } = useParams();
  const token = useMemo(() => localStorage.getItem("token"), []);
  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token]
  );

  const initialPatientName = String(
    location.state?.patientName || new URLSearchParams(location.search).get("patient_name") || ""
  ).trim();
  const preferredAgentSlug = String(
    location.state?.selectedAgentSlug ||
      new URLSearchParams(location.search).get("agent_slug") ||
      ""
  ).trim();

  const [profile, setProfile] = useState(null);
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    if (!patientId || !token) {
      setLoading(false);
      setError("Worker session or resident context is missing.");
      return;
    }

    setLoading(true);
    try {
      const [profileRes, agentsRes] = await Promise.all([
        axios.get(
          `${config.API_BASE_URL}${config.WORKER.ELDER_PROFILE(patientId)}`,
          { headers: authHeaders }
        ),
        axios.get(`${config.API_BASE_URL}${config.WORKER_AGENTS.LIST}`, {
          headers: authHeaders
        })
      ]);

      setProfile(profileRes.data || null);
      setAgents(Array.isArray(agentsRes.data?.agents) ? agentsRes.data.agents : []);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load resident care profile.");
      setProfile(null);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, patientId, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const patientName = useMemo(() => {
    if (profile?.patient?.firstName || profile?.patient?.lastName) {
      return [profile?.patient?.firstName, profile?.patient?.lastName]
        .filter(Boolean)
        .join(" ");
    }
    return initialPatientName || "Selected resident";
  }, [initialPatientName, profile?.patient?.firstName, profile?.patient?.lastName]);

  const launchWorkspace = (agentSlug = "") => {
    const params = new URLSearchParams({
      patient_id: patientId || "",
      patient_name: patientName
    });
    if (agentSlug) {
      params.set("agent_slug", agentSlug);
    }

    navigate(`/worker-agent-chat?${params.toString()}`, {
      state: {
        patientId,
        patientName,
        selectedAgentSlug: agentSlug || ""
      }
    });
  };

  const medications = profile?.careFields?.medications || [];
  const chronicConditions = profile?.careFields?.chronicConditions || [];
  const familyContact = {
    name: profile?.careFields?.familyContactName || null,
    phone: profile?.careFields?.familyContactPhone || null,
    email: profile?.careFields?.familyContactEmail || null
  };
  const noteItems = [
    {
      label: "Context",
      value: profile?.careFields?.notesContext || null
    },
    {
      label: "Emotional",
      value: profile?.careFields?.notesEmotional || null
    },
    {
      label: "Physical",
      value: profile?.careFields?.notesPhysical || null
    }
  ].filter((item) => item.value);

  if (loading) {
    return (
      <WorkerSidebarShell activeItem="patients">
        <div style={loadingCard}>Loading resident care profile...</div>
      </WorkerSidebarShell>
    );
  }

  return (
    <WorkerSidebarShell activeItem="patients">
      <div style={page}>
        <div style={hero}>
          <div style={heroContent}>
            <div style={eyebrow}>Worker Care Surface</div>
            <h1 style={heroTitle}>{patientName}</h1>
            <p style={heroCopy}>
              Review current care context, see what your worker agents can do for this
              resident, and jump into the agent workspace with the resident scope already set.
            </p>
            <div style={heroMetaRow}>
              <span style={metaPill}>
                {profile?.patient?.city || "-"}, {profile?.patient?.country || "-"}
              </span>
              <span style={metaPill}>
                {profile?.patient?.spokenLanguage || "Language not set"}
              </span>
              <span style={metaPill}>
                {profile?.patient?.gender || "Gender not set"}
              </span>
              <span style={metaPill}>
                {profile?.careContext?.groups?.length || 0} group
                {(profile?.careContext?.groups?.length || 0) === 1 ? "" : "s"}
              </span>
            </div>
          </div>

          <div style={heroActions}>
            <button
              type="button"
              style={secondaryButton}
              onClick={() => navigate("/activatedpatients")}
            >
              <FaArrowLeft />
              Back To People I Support
            </button>
            <button
              type="button"
              style={primaryButton}
              onClick={() => launchWorkspace(preferredAgentSlug)}
            >
              <FaComments />
              Open Agent Workspace
            </button>
          </div>
        </div>

        {error ? <div style={errorBanner}>{error}</div> : null}

        <section style={careStrip}>
          <CareCard
            icon={<FaHeartbeat />}
            title="Medications"
            subtitle={`${profile?.careSummary?.medicationCount || 0} recorded`}
            items={medications}
            emptyText="No medications recorded yet."
          />
          <CareCard
            icon={<FaUsers />}
            title="Conditions"
            subtitle={`${profile?.careSummary?.chronicConditionCount || 0} tracked`}
            items={chronicConditions}
            emptyText="No chronic conditions recorded."
          />
          <CareCard
            icon={<FaPhoneAlt />}
            title="Family Contact"
            subtitle={
              familyContact.name || familyContact.phone || familyContact.email
                ? "Primary contact available"
                : "No primary family contact recorded"
            }
            items={[
              familyContact.name ? `Name: ${familyContact.name}` : null,
              familyContact.phone ? `Phone: ${familyContact.phone}` : null,
              familyContact.email ? `Email: ${familyContact.email}` : null
            ].filter(Boolean)}
            emptyText="No family contact recorded."
          />
          <CareCard
            icon={<FaNotesMedical />}
            title="Care Notes"
            subtitle={`${profile?.careSummary?.noteCount || 0} sections filled`}
            items={noteItems.map((item) => `${item.label}: ${item.value}`)}
            emptyText="No care notes recorded yet."
          />
        </section>

        <div style={contentGrid}>
          <section style={panel}>
            <div style={sectionHeader}>
              <div>
                <div style={sectionEyebrow}>Role Bundles</div>
                <h2 style={sectionTitle}>Agents available for this resident</h2>
                <p style={sectionCopy}>
                  These are your worker-owned agents. The tool list is the capability
                  contract: what the agent can read and what it can change for this resident.
                </p>
              </div>
              <button
                type="button"
                style={ghostButton}
                onClick={() => navigate("/worker-agents")}
              >
                <FaRobot />
                Manage Agent Library
              </button>
            </div>

            {agents.length === 0 ? (
              <div style={emptyState}>
                <p style={emptyTitle}>No worker agents are available yet.</p>
                <p style={emptyCopy}>
                  Add an agent in your library first, then come back here to run it in
                  this resident&apos;s context.
                </p>
              </div>
            ) : (
              <div style={agentList}>
                {agents.map((agent) => {
                  const tools = Array.isArray(agent.tools) ? agent.tools : [];
                  const capabilityLabels = tools.map(
                    (toolName) => TOOL_LABELS[toolName] || toolName.replace(/_/g, " ")
                  );
                  const canWrite = tools.some((toolName) => WRITE_TOOLS.has(toolName));
                  const isPreferred = preferredAgentSlug && agent.slug === preferredAgentSlug;

                  return (
                    <article
                      key={agent.slug}
                      style={{
                        ...agentCard,
                        ...(isPreferred ? preferredAgentCard : null)
                      }}
                    >
                      <div style={agentTopRow}>
                        <div>
                          <div style={agentNameRow}>
                            <FaRobot />
                            <h3 style={agentName}>{agent.name}</h3>
                          </div>
                          <p style={agentDescription}>
                            {agent.description || "No description provided."}
                          </p>
                        </div>
                        <div style={badgeColumn}>
                          <span style={scopeBadge}>Read elder context</span>
                          <span
                            style={{
                              ...scopeBadge,
                              ...(canWrite ? writeBadge : readOnlyBadge)
                            }}
                          >
                            {canWrite ? "Can update care fields" : "Read-only bundle"}
                          </span>
                        </div>
                      </div>

                      <div style={capabilityWrap}>
                        {capabilityLabels.length > 0 ? (
                          capabilityLabels.map((label) => (
                            <span key={`${agent.slug}-${label}`} style={capabilityChip}>
                              {label}
                            </span>
                          ))
                        ) : (
                          <span style={capabilityChipMuted}>No declared tools</span>
                        )}
                      </div>

                      <div style={agentMetaRow}>
                        <span>Model: {agent.model || "N/A"}</span>
                        <span>Version: {agent.version || "N/A"}</span>
                        <span>{tools.length} tool{tools.length === 1 ? "" : "s"}</span>
                      </div>

                      <div style={agentActions}>
                        <button
                          type="button"
                          style={secondaryButton}
                          onClick={() => launchWorkspace(agent.slug)}
                        >
                          <FaComments />
                          Work With Agent
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>

          <aside style={sideColumn}>
            <section style={panel}>
              <div style={sectionEyebrow}>Support Context</div>
              <h2 style={sectionTitle}>Current care team</h2>
              <div style={supportList}>
                {(profile?.careContext?.activeWorkers || []).map((worker) => (
                  <div key={worker.workerID} style={supportCard}>
                    <div style={supportCardIcon}>
                      <FaUserNurse />
                    </div>
                    <div>
                      <div style={supportName}>
                        {[worker.firstName, worker.lastName].filter(Boolean).join(" ") ||
                          worker.email ||
                          "Worker"}
                      </div>
                      <div style={supportMeta}>
                        {worker.status || "status not set"} ·{" "}
                        {worker.spokenLanguage || "language not set"}
                      </div>
                    </div>
                  </div>
                ))}
                {!profile?.careContext?.activeWorkers?.length ? (
                  <div style={emptyInlineState}>No active workers recorded.</div>
                ) : null}
              </div>
            </section>

            <section style={panel}>
              <div style={sectionEyebrow}>Change Timeline</div>
              <h2 style={sectionTitle}>Recent care updates</h2>
              <div style={timeline}>
                {(profile?.recentCareUpdates || []).map((update) => (
                  <div key={update.id} style={timelineItem}>
                    <div style={timelineDot} />
                    <div style={timelineContent}>
                      <div style={timelineSummary}>{update.summary}</div>
                      <div style={timelineMeta}>
                        {update.workerName || update.actorType || "Worker"} ·{" "}
                        {formatTimestamp(update.createdAt)}
                      </div>
                      {update.agentSlug ? (
                        <div style={timelineToolMeta}>
                          via {update.agentSlug}
                          {update.toolName ? ` / ${update.toolName}` : ""}
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
                {!profile?.recentCareUpdates?.length ? (
                  <div style={emptyInlineState}>
                    No agent-assisted care updates recorded yet.
                  </div>
                ) : null}
              </div>
            </section>
          </aside>
        </div>
      </div>
    </WorkerSidebarShell>
  );
};

function CareCard({ icon, title, subtitle, items, emptyText }) {
  return (
    <article style={careCard}>
      <div style={careCardIcon}>{icon}</div>
      <div style={careCardTitle}>{title}</div>
      <div style={careCardSubtitle}>{subtitle}</div>
      <div style={careItemList}>
        {(items || []).length ? (
          items.map((item) => (
            <span key={`${title}-${item}`} style={careItemChip}>
              {item}
            </span>
          ))
        ) : (
          <span style={careItemEmpty}>{emptyText}</span>
        )}
      </div>
    </article>
  );
}

function formatTimestamp(value) {
  if (!value) return "unknown time";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "unknown time";
  return date.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

export default WorkerElderProfilePage;

const page = {
  display: "grid",
  gap: "20px"
};

const loadingCard = {
  background: "#fff",
  borderRadius: "18px",
  border: "1px solid #d9e6ef",
  padding: "32px",
  color: "#12344d",
  boxShadow: "0 12px 30px rgba(14, 85, 128, 0.08)"
};

const hero = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "18px",
  flexWrap: "wrap",
  padding: "28px",
  borderRadius: "24px",
  background:
    "linear-gradient(135deg, rgba(14,85,128,1) 0%, rgba(18,106,155,1) 48%, rgba(78,165,217,0.92) 100%)",
  color: "#fff",
  boxShadow: "0 18px 40px rgba(8, 54, 84, 0.24)"
};

const heroContent = {
  maxWidth: "760px"
};

const eyebrow = {
  fontSize: "12px",
  fontWeight: 800,
  letterSpacing: "0.18em",
  textTransform: "uppercase",
  opacity: 0.84,
  marginBottom: "10px"
};

const heroTitle = {
  margin: 0,
  fontSize: "34px",
  lineHeight: 1.05
};

const heroCopy = {
  margin: "12px 0 0",
  maxWidth: "720px",
  lineHeight: 1.65,
  color: "rgba(255,255,255,0.9)"
};

const heroMetaRow = {
  display: "flex",
  flexWrap: "wrap",
  gap: "10px",
  marginTop: "18px"
};

const metaPill = {
  padding: "9px 12px",
  borderRadius: "999px",
  background: "rgba(255,255,255,0.16)",
  border: "1px solid rgba(255,255,255,0.18)",
  fontSize: "13px"
};

const heroActions = {
  display: "grid",
  gap: "12px",
  minWidth: "220px"
};

const primaryButton = {
  border: "none",
  borderRadius: "14px",
  padding: "12px 16px",
  background: "#f7b731",
  color: "#12344d",
  fontWeight: 700,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "10px",
  cursor: "pointer"
};

const secondaryButton = {
  border: "1px solid rgba(18, 52, 77, 0.12)",
  borderRadius: "14px",
  padding: "12px 16px",
  background: "#fff",
  color: "#0E5580",
  fontWeight: 700,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "10px",
  cursor: "pointer"
};

const ghostButton = {
  ...secondaryButton,
  borderColor: "#d5e4ef"
};

const errorBanner = {
  background: "#fff1f2",
  color: "#991b1b",
  border: "1px solid #fecdd3",
  borderRadius: "14px",
  padding: "14px 16px"
};

const careStrip = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "14px"
};

const careCard = {
  background: "#fff",
  borderRadius: "20px",
  border: "1px solid #dce7f1",
  padding: "18px",
  boxShadow: "0 10px 26px rgba(15, 23, 42, 0.05)",
  minHeight: "214px",
  display: "grid",
  alignContent: "start",
  gap: "10px"
};

const careCardIcon = {
  width: "42px",
  height: "42px",
  borderRadius: "14px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(14,85,128,0.12)",
  color: "#0E5580",
  fontSize: "18px"
};

const careCardTitle = {
  color: "#12344d",
  fontSize: "18px",
  fontWeight: 700
};

const careCardSubtitle = {
  color: "#597184",
  fontSize: "14px"
};

const careItemList = {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px"
};

const careItemChip = {
  background: "#eef6fb",
  border: "1px solid #d8e7f2",
  color: "#1f4257",
  borderRadius: "999px",
  padding: "8px 10px",
  fontSize: "13px",
  lineHeight: 1.35
};

const careItemEmpty = {
  color: "#70889a",
  fontSize: "14px",
  lineHeight: 1.5
};

const contentGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
  gap: "18px",
  alignItems: "start"
};

const panel = {
  background: "#fff",
  borderRadius: "20px",
  border: "1px solid #dce7f1",
  padding: "22px",
  boxShadow: "0 12px 28px rgba(15, 23, 42, 0.05)"
};

const sideColumn = {
  display: "grid",
  gap: "18px"
};

const sectionHeader = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "16px",
  flexWrap: "wrap",
  marginBottom: "18px"
};

const sectionEyebrow = {
  color: "#0E5580",
  fontSize: "12px",
  fontWeight: 800,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  marginBottom: "8px"
};

const sectionTitle = {
  margin: 0,
  color: "#12344d",
  fontSize: "24px"
};

const sectionCopy = {
  margin: "10px 0 0",
  color: "#5f7283",
  lineHeight: 1.6,
  maxWidth: "760px"
};

const emptyState = {
  border: "1px dashed #cfe0ec",
  borderRadius: "18px",
  padding: "26px",
  background: "#f8fbfd"
};

const emptyTitle = {
  margin: 0,
  color: "#12344d",
  fontSize: "18px",
  fontWeight: 700
};

const emptyCopy = {
  margin: "10px 0 0",
  color: "#617282",
  lineHeight: 1.6
};

const agentList = {
  display: "grid",
  gap: "14px"
};

const agentCard = {
  border: "1px solid #dce7f1",
  borderRadius: "18px",
  padding: "18px",
  background: "#fbfdff",
  display: "grid",
  gap: "14px"
};

const preferredAgentCard = {
  borderColor: "#4EA5D9",
  boxShadow: "0 0 0 3px rgba(78, 165, 217, 0.15)"
};

const agentTopRow = {
  display: "flex",
  justifyContent: "space-between",
  gap: "16px",
  flexWrap: "wrap"
};

const agentNameRow = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  color: "#0E5580"
};

const agentName = {
  margin: 0,
  color: "#12344d",
  fontSize: "20px"
};

const agentDescription = {
  margin: "8px 0 0",
  color: "#5c7081",
  lineHeight: 1.55
};

const badgeColumn = {
  display: "grid",
  gap: "8px",
  alignContent: "start"
};

const scopeBadge = {
  borderRadius: "999px",
  padding: "8px 10px",
  fontSize: "12px",
  fontWeight: 700,
  background: "#eef6fb",
  color: "#0E5580",
  textAlign: "center"
};

const writeBadge = {
  background: "#fff4d6",
  color: "#935f00"
};

const readOnlyBadge = {
  background: "#ecfdf5",
  color: "#166534"
};

const capabilityWrap = {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px"
};

const capabilityChip = {
  borderRadius: "999px",
  padding: "8px 10px",
  fontSize: "13px",
  background: "#f1f5f9",
  color: "#28465b",
  border: "1px solid #dbe7ef"
};

const capabilityChipMuted = {
  ...capabilityChip,
  color: "#70889a"
};

const agentMetaRow = {
  display: "flex",
  flexWrap: "wrap",
  gap: "12px",
  color: "#607484",
  fontSize: "13px"
};

const agentActions = {
  display: "flex",
  justifyContent: "flex-start"
};

const supportList = {
  display: "grid",
  gap: "10px"
};

const supportCard = {
  display: "grid",
  gridTemplateColumns: "42px 1fr",
  gap: "12px",
  alignItems: "center",
  padding: "12px",
  borderRadius: "14px",
  background: "#f7fbfe",
  border: "1px solid #dce8f1"
};

const supportCardIcon = {
  width: "42px",
  height: "42px",
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(14,85,128,0.12)",
  color: "#0E5580"
};

const supportName = {
  color: "#12344d",
  fontWeight: 700
};

const supportMeta = {
  color: "#667b8b",
  fontSize: "13px",
  marginTop: "4px"
};

const timeline = {
  display: "grid",
  gap: "14px"
};

const timelineItem = {
  display: "grid",
  gridTemplateColumns: "12px 1fr",
  gap: "12px",
  alignItems: "start"
};

const timelineDot = {
  width: "12px",
  height: "12px",
  marginTop: "6px",
  borderRadius: "999px",
  background: "#4EA5D9",
  boxShadow: "0 0 0 4px rgba(78, 165, 217, 0.18)"
};

const timelineContent = {
  paddingBottom: "12px",
  borderBottom: "1px solid #edf3f7"
};

const timelineSummary = {
  color: "#12344d",
  fontWeight: 700,
  lineHeight: 1.45
};

const timelineMeta = {
  color: "#637887",
  fontSize: "13px",
  marginTop: "4px"
};

const timelineToolMeta = {
  color: "#0E5580",
  fontSize: "12px",
  marginTop: "6px",
  letterSpacing: "0.02em"
};

const emptyInlineState = {
  color: "#6d8393",
  fontSize: "14px",
  lineHeight: 1.5
};
